import React from "react";
import { View, Text, StyleSheet, Image, Platform } from "react-native";
import type { ImageSourcePropType, ImageStyle, StyleProp } from "react-native";
import type { CSSProperties } from 'react';

type BirkenstockSizeSummary = {
  size: string;
  width: string;
  secondarySize?: string;
  narrowAlternative?: { size: string; width: "Narrow" };
};

export type BirkenstockSummary = {
  sandal: BirkenstockSizeSummary;
  shoe: BirkenstockSizeSummary;
  left: { length: number; width: number };
  right: { length: number; width: number };
  scanId: string;
};

const sealAsset = require('../../assets/images/Seal_Pantone_blue.png') as ImageSourcePropType;
const sandalAsset = require('../../assets/images/sandal-icon.png') as ImageSourcePropType;
const shoeAsset = require('../../assets/images/shoe-icon.png') as ImageSourcePropType;

const sealWebStyle: CSSProperties = {
  width: 56,
  height: 56,
  margin: '0 auto 8px',
  objectFit: 'contain',
  display: 'block',
};

const footIconWebStyle: CSSProperties = {
  width: '70%',
  height: 'auto',
  maxWidth: 80,
  margin: '0 auto 6px',
  objectFit: 'contain',
  display: 'block',
};

const resolveAssetUri = (asset: ImageSourcePropType) => {
  if (typeof asset === 'number') {
    const resolver = (Image as typeof Image & { resolveAssetSource?: (source: ImageSourcePropType) => { uri?: string } | null }).resolveAssetSource;
    const resolved = resolver ? resolver(asset) : null;
    if (resolved?.uri) {
      return resolved.uri;
    }
  }

  if (typeof asset === 'string') {
    return asset;
  }

  if (typeof asset === 'object' && asset) {
    const maybeUri = (asset as { uri?: unknown }).uri;
    if (typeof maybeUri === 'string') {
      return maybeUri;
    }

    const maybeDefault = (asset as { default?: unknown }).default;
    if (typeof maybeDefault === 'string') {
      return maybeDefault;
    }
  }

  return '';
};

const renderAssetImage = (
  asset: ImageSourcePropType,
  alt: string,
  nativeStyle: StyleProp<ImageStyle>,
  webStyle: CSSProperties,
) => {
  if (Platform.OS === 'web') {
    const uri = resolveAssetUri(asset);
    if (!uri) {
      return null;
    }

    return <img src={uri} alt={alt} style={webStyle} />;
  }

  return <Image source={asset} style={nativeStyle} resizeMode="contain" />;
};

const BirkenstockLabel: React.FC<{ summary: BirkenstockSummary }> = ({ summary }) => {
  const normalizeWidth = (value: string | undefined) => (value || '').replace(/\s+/g, '').toLowerCase();

  const sandalWidth = normalizeWidth(summary.sandal.width);
  const shoeWidth = normalizeWidth(summary.shoe.width);

  const renderWidthOption = (label: string, activeLabel: string) => {
    const normalizedLabel = normalizeWidth(label);
    const isActive = activeLabel === normalizedLabel;

    return (
      <View style={styles.widthOption}>
        <View style={styles.checkbox}>
          {isActive && <Text style={styles.checkboxMark}>X</Text>}
        </View>
        <Text style={styles.widthLabel}>{label}</Text>
      </View>
    );
  };

  const placeholder = '--';
  const formatSize = (value: string | undefined) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : placeholder;
  };
  const formatMeasurement = (value: number | null | undefined) => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      return `${value}`;
    }

    return placeholder;
  };
  const formatScanId = (value: string | undefined) => {
    const trimmed = value?.trim();
    return trimmed ? trimmed.toUpperCase() : placeholder;
  };

  return (
    <View style={styles.card}>
      {renderAssetImage(sealAsset, 'Birkenstock seal', styles.seal, sealWebStyle)}

      <View style={styles.iconRow}>
        <View style={styles.iconHalf}>
          {renderAssetImage(sandalAsset, 'Sandal silhouette', styles.footIcon, footIconWebStyle)}
        </View>
        <View style={styles.iconHalf}>
          {renderAssetImage(shoeAsset, 'Shoe silhouette', styles.footIcon, footIconWebStyle)}
        </View>
      </View>

      <View style={styles.sizeRow}>
        <View style={styles.sizeHalf}>
          <Text style={styles.sizeValue}>{formatSize(summary.sandal.size)}</Text>
          <View style={styles.sizeUnderline} />
        </View>
        <View style={styles.sizeCenter}>
          <Text style={styles.sizeLabel}>SIZE</Text>
        </View>
        <View style={styles.sizeHalf}>
          <Text style={styles.sizeValue}>{formatSize(summary.shoe.size)}</Text>
          <View style={styles.sizeUnderline} />
        </View>
      </View>

      <View style={styles.widthRow}>
        <View style={styles.widthColumn}>
          {renderWidthOption('regular', sandalWidth)}
          {renderWidthOption('narrow', sandalWidth)}
        </View>
        <View style={styles.widthColumn}>
          {renderWidthOption('regular', shoeWidth)}
          {renderWidthOption('narrow', shoeWidth)}
        </View>
      </View>

      <View style={styles.measurementsTable}>
        <View style={styles.measurementsHeaderRow}>
          <View style={styles.measurementLabelSpacer} />
          <Text style={styles.footLetter}>L</Text>
          <Text style={styles.footLetter}>R</Text>
        </View>
        <View style={styles.measurementRow}>
          <Text style={styles.measurementLabel}>length (cm):</Text>
          <Text style={styles.measurementValue}>{formatMeasurement(summary.left.length)/10}</Text>
          <Text style={styles.measurementValue}>{formatMeasurement(summary.right.length)/10}</Text>
        </View>
        <View style={styles.measurementRow}>
          <Text style={styles.measurementLabel}>width (cm):</Text>
          <Text style={styles.measurementValue}>{formatMeasurement(summary.left.width)/10}</Text>
          <Text style={styles.measurementValue}>{formatMeasurement(summary.right.width)/10}</Text>
        </View>
      </View>

      <View style={styles.scanIdRow}>
        <Text style={styles.scanLabel}>SCAN ID:</Text>
        <Text style={styles.scanValue}>{formatScanId(summary.scanId)}</Text>
      </View>
    </View>
  );
};

const primaryColor = '#000000';
const valueColor = '#000000';

const styles = StyleSheet.create({
  /** Match final printout: 51 mm × 82 mm (useBirkenstockPrint). */
  card: {
    width: 210,
    aspectRatio: 51 / 82,
    borderWidth: 0,
    backgroundColor: '#fff',
    paddingHorizontal: 12,
    paddingVertical: 10,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  seal: {
    width: 56,
    height: 56,
    alignSelf: 'center',
    marginBottom: 6,
  },
  iconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 4,
  },
  iconHalf: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
    minHeight: 60,
  },
  footIcon: {
    width: '80%',
    maxWidth: 90,
    aspectRatio: 140 / 60,
    height: undefined,
  },
  sizeRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 6,
  },
  sizeHalf: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  sizeCenter: {
    width: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sizeUnderline: {
    width: 56,
    height: 1,
    backgroundColor: primaryColor,
  },
  sizeValue: {
    fontFamily: 'Geist-Medium',
    fontSize: 12,
    color: valueColor,
    marginBottom: 3,
  },
  widthRow: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  widthColumn: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  widthOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 3,
  },
  checkbox: {
    width: 11,
    height: 11,
    borderWidth: 1,
    borderColor: primaryColor,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1,
  },
  checkboxMark: {
    color: valueColor,
    fontSize: 9,
    lineHeight: 9,
    textAlign: 'center',
    fontFamily: 'Geist-Medium',
  },
  widthLabel: {
    fontSize: 9,
    color: primaryColor,
    fontFamily: 'Geist-Medium',
    marginLeft: 4,
    textTransform: 'lowercase',
  },
  sizeLabel: {
    fontFamily: 'Birkentype',
    fontSize: 11,
    letterSpacing: 2,
    color: primaryColor,
    marginHorizontal: 6,
  },
  measurementsTable: {
    width: '100%',
    marginBottom: 5,
  },
  measurementsHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 5,
  },
  footLetter: {
    fontFamily: 'Birkentype',
    fontSize: 11,
    color: primaryColor,
    textAlign: 'center',
    width: 48,
    marginLeft: 6,
  },
  measurementLabelSpacer: {
    width: 56,
  },
  measurementRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 5,
  },
  measurementLabel: {
    fontFamily: 'Geist-Medium',
    fontSize: 9,
    color: primaryColor,
    textTransform: 'lowercase',
    width: 56,
  },
  measurementValue: {
    fontFamily: 'Geist-Medium',
    fontSize: 11,
    color: valueColor,
    borderBottomWidth: 1,
    borderBottomColor: primaryColor,
    paddingBottom: 2,
    textAlign: 'center',
    width: 48,
    marginLeft: 6,
  },
  scanIdRow: {
    width: '100%',
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  scanLabel: {
    fontFamily: 'Geist-Medium',
    fontSize: 9,
    color: primaryColor,
    letterSpacing: 1,
    marginRight: 8,
  },
  scanValue: {
    fontFamily: 'Geist-Medium',
    fontSize: 12,
    color: valueColor,
    borderBottomWidth: 1,
    borderBottomColor: primaryColor,
    paddingBottom: 2,
    flex: 1,
    textAlign: 'center',
    letterSpacing: 1,
  },
});

export default BirkenstockLabel;
